import { bffApi } from "../../autogen/bff-api";
import type { AgentConversationDto } from "../../autogen/bff-api";
import type { AppDispatch } from "../redux/store";
import type { WireMessage } from "./agentMemory";
import type { AgentTurn, EntityHighlight, ToolStep } from "./agentTypes";

export const CONVERSATION_TITLE_MAX_LENGTH = 40;
const FALLBACK_TITLE = "Ny samtale";

export type PersistedTurn = {
  user: string;
  steps: ToolStep[];
  note: string | null;
  answerText: string | null;
  entities?: EntityHighlight[];
};

export type ClientState = {
  turns: PersistedTurn[];
  entityIndex: Record<string, Record<string, unknown>>;
  model?: string;
};

export const toPersistedTurn = (turn: AgentTurn): PersistedTurn => ({
  user: turn.user,
  steps: turn.steps,
  note: turn.note,
  answerText: turn.answerText,
  ...(turn.entities ? { entities: turn.entities } : {}),
});

export const toRestoredTurn = (turn: PersistedTurn): AgentTurn => ({
  user: turn.user,
  steps: turn.steps ?? [],
  note: turn.note ?? null,
  answerText: turn.answerText ?? null,
  entities: turn.entities,
});

export const buildClientState = (
  turns: AgentTurn[],
  entityIndex: Map<string, Record<string, unknown>>,
  model?: string,
): ClientState => ({
  turns: turns.map(toPersistedTurn),
  entityIndex: Object.fromEntries(entityIndex),
  ...(model ? { model } : {}),
});

export const serializeClientState = (state: ClientState): string => JSON.stringify(state);

export const parseClientState = (raw: string): ClientState => {
  try {
    const parsed = JSON.parse(raw) as Partial<ClientState> | null;
    return { turns: parsed?.turns ?? [], entityIndex: parsed?.entityIndex ?? {}, model: parsed?.model };
  } catch {
    return { turns: [], entityIndex: {} };
  }
};

export const restoreEntityIndex = (
  record: Record<string, Record<string, unknown>>,
): Map<string, Record<string, unknown>> => new Map(Object.entries(record));

export const firstUserContent = (messages: WireMessage[]): string | undefined =>
  messages.find((m) => m.role === "user")?.content ?? undefined;

export const deriveTitle = (firstUserMessage: string | null | undefined): string => {
  const trimmed = (firstUserMessage ?? "").trim();
  if (!trimmed) {
    return FALLBACK_TITLE;
  }
  return trimmed.length > CONVERSATION_TITLE_MAX_LENGTH
    ? trimmed.slice(0, CONVERSATION_TITLE_MAX_LENGTH).trimEnd()
    : trimmed;
};

// The fetch must hold a cache subscription until unwrap has delivered: with subscribe: false the
// entry is evicted before the thunk resolves and unwrap yields undefined instead of the DTO.
export const getConversation = async (dispatch: AppDispatch, conversationId: string): Promise<AgentConversationDto> => {
  const subscription = dispatch(
    bffApi.endpoints.getAgentConversation.initiate({ conversationId }, { forceRefetch: true }),
  );
  try {
    return await subscription.unwrap();
  } finally {
    subscription.unsubscribe();
  }
};

export const createConversation = (dispatch: AppDispatch, title: string): Promise<AgentConversationDto> =>
  dispatch(bffApi.endpoints.createAgentConversation.initiate({ createAgentConversationRequest: { title } })).unwrap();

export type ConversationUpdate = { title: string; messages: WireMessage[]; clientState: string };

export const updateConversation = (
  dispatch: AppDispatch,
  conversationId: string,
  update: ConversationUpdate,
): Promise<AgentConversationDto> =>
  dispatch(
    bffApi.endpoints.updateAgentConversation.initiate({ conversationId, updateAgentConversationRequest: update }),
  ).unwrap();

export const deleteConversation = (dispatch: AppDispatch, conversationId: string): Promise<unknown> =>
  dispatch(bffApi.endpoints.deleteAgentConversation.initiate({ conversationId })).unwrap();

export type ConversationSaveTriggers = {
  create: (title: string) => Promise<AgentConversationDto>;
  update: (conversationId: string, update: ConversationUpdate) => Promise<unknown>;
};

export type ConversationSaveInput = {
  activeConversationId: string | null;
  messages: WireMessage[];
  clientState: ClientState;
};

// The first save of a fresh conversation creates it (title = first user message) then updates it with
// the wire array + client state; every later save is a plain update. onCreated fires the moment the id
// exists so a failed update never re-creates a duplicate on the next retry.
export const saveConversation = async (
  triggers: ConversationSaveTriggers,
  input: ConversationSaveInput,
  onCreated?: (conversationId: string) => void,
): Promise<string> => {
  const title = deriveTitle(firstUserContent(input.messages));
  let conversationId = input.activeConversationId;
  if (conversationId === null) {
    conversationId = (await triggers.create(title)).id;
    onCreated?.(conversationId);
  }
  await triggers.update(conversationId, {
    title,
    messages: input.messages,
    clientState: serializeClientState(input.clientState),
  });
  return conversationId;
};
